class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/**
 * customização: helper reutilizável de Quick Add (prompts #5/#15).
 * Adiciona variante via /cart/add.js, atualiza drawer + bubble sem fechar o drawer.
 */
window.DawnCart = window.DawnCart || {};

window.DawnCart.formatMoney = function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const formatString = format || '${{amount}}';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (isNaN(number) || number == null) return '0';
    const parts = (Number(number) / 100.0).toFixed(precision).split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? `${dollars}${decimal}${parts[1]}` : dollars;
  }

  const match = formatString.match(placeholderRegex);
  if (!match) return formatString;

  switch (match[1]) {
    case 'amount':
      return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 2));
    case 'amount_no_decimals':
      return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 0));
    case 'amount_with_comma_separator':
      return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 2, '.', ','));
    case 'amount_no_decimals_with_comma_separator':
      return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 0, '.', ','));
    case 'amount_with_apostrophe_separator':
      return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 2, "'", '.'));
    default:
      return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 2));
  }
};

window.DawnCart.addVariantAndRefresh = async function addVariantAndRefresh(variantId, quantity = 1) {
  const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', quantity);

  if (cart?.getSectionsToRender) {
    formData.append(
      'sections',
      cart
        .getSectionsToRender()
        .map((section) => section.id)
        .join(',')
    );
    formData.append('sections_url', window.location.pathname);
  }

  config.body = formData;

  const response = await fetch(`${routes.cart_add_url}`, config);
  const data = await response.json();

  if (data.status) {
    throw new Error(data.description || data.message || 'Unable to add to cart');
  }

  if (cart) {
    cart.classList.remove('is-empty');
    if (typeof cart.renderContents === 'function') {
      cart.renderContents(data);
    }
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'dawn-cart-quick-add',
    productVariantId: variantId,
    cartData: data,
  });

  return data;
};

/**
 * customização: recomendações no drawer com carrossel CSS Scroll Snap isolado (Regras #10/#12).
 */
class CartDrawerRecommendations extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.cart-drawer-recommendations__track');
    this.statusEl = this.querySelector('[data-recommendations-status]');
    this.productId = this.dataset.productId;
    this.limit = this.dataset.limit || '4';
    this.moneyFormat = this.dataset.moneyFormat;
    this.addLabel = this.dataset.addLabel || 'ADD';
    this.sizeLabel = this.dataset.sizeLabel || 'Size';
    this.oneSizeLabel = this.dataset.oneSizeLabel || 'One Size';
    this.statusTemplate = this.dataset.statusTemplate || '';

    if (!this.productId || !this.dataset.url || !this.track) {
      this.hidden = true;
      return;
    }

    this.loadRecommendations();
  }

  disconnectedCallback() {
    this.slideObserver?.disconnect();
  }

  async loadRecommendations() {
    const url = `${this.dataset.url}?product_id=${this.productId}&limit=${this.limit}&intent=related`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Recommendations request failed');
      const data = await response.json();
      const products = (data.products || []).filter((product) => String(product.id) !== String(this.productId));

      if (!products.length) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.renderProducts(products);
      this.bindSlideObserver();
    } catch (error) {
      console.error(error);
      this.hidden = true;
    }
  }

  renderProducts(products) {
    const html = products.map((product) => this.cardHTML(product)).join('');
    if (!html.trim()) {
      this.hidden = true;
      return;
    }

    this.track.innerHTML = html;
    this.track.querySelectorAll('[data-cart-rec-add]').forEach((button) => {
      button.addEventListener('click', this.onAddClick.bind(this));
    });
    this.track.querySelectorAll('[data-cart-rec-variant]').forEach((select) => {
      select.addEventListener('change', this.onVariantChange.bind(this));
    });
  }

  cardHTML(product) {
    const variants = (product.variants || []).filter((variant) => variant.available);
    if (!variants.length) return '';

    const imageSrc = this.resolveImageSrc(product, variants[0]);
    const priceHTML = this.priceHTML(product, variants[0]);
    const sizeOption = this.findSizeOption(product);
    const selectHTML = this.variantSelectHTML(product, variants, sizeOption);
    const initialVariantId = this.initialVariantId(product, variants, sizeOption);

    return `
      <article class="cart-upsell-card" role="listitem" data-product-id="${product.id}">
        <a class="cart-upsell-card__media" href="${this.escapeAttr(product.url)}">
          ${
            imageSrc
              ? `<img class="cart-upsell-card__image" src="${this.escapeAttr(
                  imageSrc
                )}" alt="${this.escapeAttr(product.title)}" width="110" height="138" loading="lazy">`
              : ''
          }
        </a>
        <a class="cart-upsell-card__name" href="${this.escapeAttr(product.url)}">${this.escapeHTML(product.title)}</a>
        <div class="cart-upsell-card__price">${priceHTML}</div>
        <div class="cart-upsell-card__actions">
          ${selectHTML}
          <button
            type="button"
            class="cart-upsell-card__add-btn"
            data-cart-rec-add
            data-variant-id="${initialVariantId}"
          >
            ${this.escapeHTML(this.addLabel)}
          </button>
        </div>
      </article>
    `;
  }

  priceHTML(product, variant) {
    const price = Number(product.price ?? variant?.price ?? 0);
    const compare = Number(product.compare_at_price ?? variant?.compare_at_price ?? 0);
    let priceFormatted = '';

    try {
      priceFormatted = window.DawnCart?.formatMoney
        ? window.DawnCart.formatMoney(price, this.moneyFormat)
        : String(price);
    } catch (error) {
      console.error(error);
      priceFormatted = String(price);
    }

    if (compare > price && compare > 0) {
      let compareFormatted = '';
      try {
        compareFormatted = window.DawnCart?.formatMoney
          ? window.DawnCart.formatMoney(compare, this.moneyFormat)
          : String(compare);
      } catch (error) {
        compareFormatted = String(compare);
      }
      return `<s class="cart-upsell-card__price-compare">${compareFormatted}</s><span class="cart-upsell-card__price-sale">${priceFormatted}</span>`;
    }

    return `<span class="cart-upsell-card__price-regular">${priceFormatted}</span>`;
  }

  resolveImageSrc(product, variant) {
    const featured = product.featured_image;
    if (typeof featured === 'string' && featured) return featured.startsWith('//') ? `https:${featured}` : featured;
    if (featured?.src) return featured.src.startsWith('//') ? `https:${featured.src}` : featured.src;
    if (typeof product.images?.[0] === 'string') {
      const src = product.images[0];
      return src.startsWith('//') ? `https:${src}` : src;
    }
    if (product.images?.[0]?.src) {
      const src = product.images[0].src;
      return src.startsWith('//') ? `https:${src}` : src;
    }
    const variantSrc = variant?.featured_image?.src || '';
    return variantSrc.startsWith('//') ? `https:${variantSrc}` : variantSrc;
  }

  initialVariantId(product, variants, sizeOption) {
    if (!sizeOption || product.has_only_default_variant || variants.length <= 1) {
      return variants[0].id;
    }
    const values = [...new Set(variants.map((variant) => variant[`option${sizeOption.position}`]).filter(Boolean))];
    if (values.length <= 1) return variants[0].id;
    const match = variants.find((variant) => variant[`option${sizeOption.position}`] === values[0]);
    return match?.id || variants[0].id;
  }

  findSizeOption(product) {
    const options = product.options || [];
    const index = options.findIndex((name) => /size|tamanho|taille|größe|talla/i.test(name));
    return index >= 0 ? { name: options[index], position: index + 1 } : null;
  }

  variantSelectHTML(product, variants, sizeOption) {
    // Sempre mostra select compacto (inclui "One Size" quando só há 1 opção)
    if (product.has_only_default_variant || variants.length <= 1) {
      return `
        <label class="visually-hidden" for="CartRec-Size-${product.id}">${this.escapeHTML(this.sizeLabel)}</label>
        <select
          id="CartRec-Size-${product.id}"
          class="cart-upsell-card__variant-select"
          data-cart-rec-variant
          aria-label="${this.escapeAttr(this.sizeLabel)}"
        >
          <option value="${variants[0].id}">${this.escapeHTML(this.oneSizeLabel)}</option>
        </select>
      `;
    }

    if (sizeOption) {
      const values = [...new Set(variants.map((variant) => variant[`option${sizeOption.position}`]).filter(Boolean))];
      if (values.length <= 1) {
        return `
          <label class="visually-hidden" for="CartRec-Size-${product.id}">${this.escapeHTML(this.sizeLabel)}</label>
          <select
            id="CartRec-Size-${product.id}"
            class="cart-upsell-card__variant-select"
            data-cart-rec-variant
            aria-label="${this.escapeAttr(this.sizeLabel)}"
          >
            <option value="${variants[0].id}">${this.escapeHTML(values[0] || this.oneSizeLabel)}</option>
          </select>
        `;
      }

      const options = values
        .map((value) => {
          const match = variants.find((variant) => variant[`option${sizeOption.position}`] === value);
          return `<option value="${match.id}">${this.escapeHTML(value)}</option>`;
        })
        .join('');

      return `
        <label class="visually-hidden" for="CartRec-Size-${product.id}">${this.escapeHTML(this.sizeLabel)}</label>
        <select
          id="CartRec-Size-${product.id}"
          class="cart-upsell-card__variant-select"
          data-cart-rec-variant
          aria-label="${this.escapeAttr(this.sizeLabel)}"
        >
          ${options}
        </select>
      `;
    }

    return this.fullVariantSelectHTML(variants);
  }

  fullVariantSelectHTML(variants) {
    const options = variants
      .map((variant) => `<option value="${variant.id}">${this.escapeHTML(variant.title)}</option>`)
      .join('');

    return `
      <label class="visually-hidden">${this.escapeHTML(this.sizeLabel)}</label>
      <select class="cart-upsell-card__variant-select" data-cart-rec-variant aria-label="${this.escapeAttr(
        this.sizeLabel
      )}">
        ${options}
      </select>
    `;
  }

  onVariantChange(event) {
    const card = event.target.closest('.cart-upsell-card');
    const button = card?.querySelector('[data-cart-rec-add]');
    if (button) button.dataset.variantId = event.target.value;
  }

  async onAddClick(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    if (!variantId || button.getAttribute('aria-disabled') === 'true') return;

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');

    try {
      await window.DawnCart.addVariantAndRefresh(variantId, 1);
    } catch (error) {
      console.error(error);
      button.removeAttribute('aria-disabled');
    } finally {
      button.classList.remove('loading');
    }
  }

  bindSlideObserver() {
    this.slideObserver?.disconnect();
    const cards = this.track.querySelectorAll('.cart-upsell-card');
    if (!cards.length || !this.statusEl) return;

    this.slideObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const index = [...cards].indexOf(visible.target) + 1;
        this.statusEl.textContent = this.statusTemplate
          .replace('[index]', String(index))
          .replace('[count]', String(cards.length));
      },
      { root: this.track, threshold: 0.6 }
    );

    cards.forEach((card) => this.slideObserver.observe(card));
  }

  escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  escapeAttr(value) {
    return this.escapeHTML(value).replace(/'/g, '&#39;');
  }
}

customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
